'use client';
import {useTableParams} from "../../core/hooks/table-params";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {INameId} from "../../core/interfaces";
import {
    createCategory,
    deleteCategory,
    getAllCategories,
    toggleCategoryCall,
    updateCategory
} from "../../services/categories";
import Loader from "../../core/components/Loader";
import {IColumn, Table} from "../../core/components/Table";
import {Pagination} from "../../core/components/Pagination";
import {Button} from "@mui/material";
import {SimpleSearchPaginationParams} from "../../core/services/utils";
import {INameModal} from "./INameModal";

export const CategoryListPage: React.FC = (): JSX.Element => {
    const {
        page,
        setPage,
        size,
        setSize,
        totalElements,
        setTotalElements,
        sortBy,
        setSortBy,

    } = useTableParams({
        defaultSortBy: 'updatedAt',
    }
    )

    const [isError, setIsError] = useState<boolean>(false)
    const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
    const [message, setMessage] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [categories, setCategories] = useState<INameId[]>([])
    const [isCheck, setIsCheck] = useState<string[]>([])
    const searchParams = useRef<{ [key: string]: string }>({})
    const timer = useRef<NodeJS.Timeout | null>(null)
    const [editMode, setEditMode] = useState<boolean>(false)
    const [currentCategory, setCurrentCategory] = useState<INameId>()
    const [showCreateCategoryModal, setShowCreateCategoryModal] = useState<boolean>(false)
    const getData = useCallback(async () => {
        try {
            setLoading(true)
            const params: SimpleSearchPaginationParams = {
                page,
                size,
                sortBy,
            };
            params.text = searchParams.current.name
            const response = await getAllCategories(params)
            setTotalElements(response.data.totalElements)
            setCategories(response.data.content)
            setPage(page + 1)
        } catch (e) {
            if (e instanceof Error) {
                setIsError(true)
                setShowMessageBox(true)
                setMessage(e.message)
            } else {
                console.error(e)
            }
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy])

    useEffect(() => {
        getData()
    }, [getData, page, size, sortBy])

    const handleSortBy = (sort: string) => {
        if (sort === sortBy) {
            setSortBy('updatedAt')
        } else {
            setSortBy(sort)
        }
    }

    const pageChange = async (newPage: number) => {
        setPage(newPage)
    }

    const handleSearchParams = useCallback(
        async (col: IColumn, q: string) => {
            if (timer.current) {
                clearTimeout(timer.current)
            }

            searchParams.current = {
                ...searchParams.current,
                [col.accessor]: q,
            }

            const newTimer = setTimeout(async () => {
                setLoading(true)
                try {
                    const params: SimpleSearchPaginationParams = {
                        page: 0,
                        size,
                        sortBy,
                    }

                    params.text = searchParams.current.name
                    const { data: res, status } = await getAllCategories(params)
                    if (status === 200) {
                        setCategories(res.content)
                        setTotalElements(res.totalElements)
                        setSize(res.size)
                        setPage(res.pageNumber + 1)
                    }
                } catch (err) {
                    console.log(err)
                } finally {
                    setLoading(false)
                }
            }, 2000)
            timer.current = newTimer
        },
        [page, size, sortBy],
    )

    function toggleCategory(id) {
        toggleCategoryCall(id).then(value => window.location.reload());
    }

    const columns: IColumn[] = [
        {
            title: 'ID',
            accessor: 'id',
            sortable: true,
        },
        {
            title: 'Name',
            accessor: 'name',
            sortable: true,
            searchable: true
        },
        {
            title: 'Visible',
            accessor: '',
            render: (row) => (
                <div
                    className="view"
                >
                    {row.visible ? 'Yes' : 'No'}
                </div>
            ),
        },
        {
            title: 'Edit',
            accessor: '',
            render: (row) => (
                <div
                    style={{ cursor: 'pointer' }}
                    className="view"
                    onClick={() => {
                        setEditMode(true)
                        setCurrentCategory(row)
                        setShowCreateCategoryModal(true)
                    }}
                >
                    Edit
                </div>
            ),
        },
        {
            title: 'Actions',
            accessor: '',
            render: (row) => (
                <div
                    style={{ cursor: 'pointer' }}
                    className="view"
                    onClick={() => {
                        toggleCategory(row.id);
                    }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => toggleCategory(row.id)}> {row.visible ? 'Hide' : 'Show'} Category
                    </Button>
                </div>
            ),
        }

    ]

    return (
        <>
            <div
                style={{
                    padding: 20,
                    backgroundColor: '#F8F8F8',
                    position: 'relative',
                }}
            >
                {loading && <Loader />}
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 20,
                    }}
                    >
                    <h2>Categories</h2>
                    <div className="d-flex gap-2">
                        <Button variant="contained" size="small" onClick={() => {
                            setEditMode(false)
                            setShowCreateCategoryModal(true)
                            setCurrentCategory(undefined)
                        }}>
                            <div className="d-flex align-items-center justify-content-center">
                                <i className="bi bi-arrow-right-square me-2 d-flex" />
                                <span>Add new Category</span>
                            </div>
                        </Button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                <Table
                    handleSearch={handleSearchParams}
                    isCheck={isCheck}
                    columns={columns}
                    handleSortBy={handleSortBy}
                    data={categories}
                />
                </div>
                <Pagination
                    size={size}
                    totalElements={totalElements}
                    setSize={setSize}
                    page={page + 1}
                    changePage={pageChange}
                />
                <INameModal
                    iNameEntity={currentCategory}
                    setEditMode={setEditMode}
                    setShowCreateModal={setShowCreateCategoryModal}
                    setAllList={setCategories}
                    showCreateModal={showCreateCategoryModal}
                    editMode={editMode}
                    page={page + 1}
                    size={size}
                    setTotalElements={setTotalElements}
                    createCall={createCategory}
                    deleteCall={deleteCategory}
                    updateCall={updateCategory}
                    getAllCall={getAllCategories}
                />

            </div>
        </>
    )
}